import Flickity from 'flickity';

const selectors = {
  slider: '[data-slider]',
  sliderItem: '[data-slide]',
  productGridItemImage: '[data-product-media-container]',
  links: 'a, button',
  flickityButton: '.flickity-button',
};

const classes = {
  carouselInactive: 'carousel--inactive',
};

const attributes = {
  sliderId: 'data-slider-id',
  showImage: 'data-slider-show-image',
  tabIndex: 'tabindex',
};

const sections = {};

class GridSlider {
  constructor(container) {
    this.container = container;
    this.columns = parseInt(this.container.dataset.columns);
    this.sliders = this.container.querySelectorAll(selectors.slider);
    this.checkSlidesSizeOnResize = () => this.checkSlidesSize();
    this.resetSliderEvent = (e) => this.resetSlider(e);
    this.flkty = [];
    this.listen();
  }

  initSlider(slider) {
    const sliderId = slider.getAttribute(attributes.sliderId);
    slider.classList.remove(classes.carouselInactive);

    if (this.flkty[sliderId] === undefined || !this.flkty[sliderId].isActive) {
      this.flkty[sliderId] = new Flickity(slider, {
        pageDots: false,
        cellSelector: selectors.sliderItem,
        cellAlign: 'left',
        groupCells: true,
        contain: true,
        wrapAround: false,
        adaptiveHeight: false,
        on: {
          ready: () => {
            this.setSliderArrowsPosition(slider);
            setTimeout(() => {
              this.changeTabIndex(slider);
            }, 0);
          },
          change: () => {
            this.changeTabIndex(slider);
          },
        },
      });
    } else {
      this.setSliderArrowsPosition(slider);
    }
  }

  destroySlider(slider) {
    const sliderId = slider.getAttribute(attributes.sliderId);
    slider.classList.add(classes.carouselInactive);

    if (typeof this.flkty[sliderId] === 'object') {
      this.flkty[sliderId].destroy();
    }
  }

  // Move slides to their initial position
  resetSlider(e) {
    const slider = e.target;
    const sliderId = slider.getAttribute(attributes.sliderId);

    if (typeof this.flkty[sliderId] === 'object') {
      this.flkty[sliderId].select(0, false, true);
    } else {
      slider.scrollTo({
        left: 0,
        behavior: 'instant',
      });
    }
  }

  checkSlidesSize() {
    if (this.sliders.length) {
      this.sliders.forEach((slider) => {
        const columns = this.columns;
        const isDesktop = window.innerWidth >= theme.sizes.large;
        const isTablet = window.innerWidth >= theme.sizes.small && window.innerWidth < theme.sizes.large;
        let itemsCount = slider.querySelectorAll(selectors.sliderItem).length;

        // If tab collection has show image enabled
        if (slider.hasAttribute(attributes.showImage)) {
          itemsCount += 1;
        }

        if ((isDesktop && itemsCount > columns) || (isTablet && itemsCount > 2)) {
          this.initSlider(slider);
        } else {
          this.destroySlider(slider);
        }
      });
    }
  }

  changeTabIndex(slider) {
    const sliderId = slider.getAttribute(attributes.sliderId);
    const selectedElementsIndex = this.flkty[sliderId].selectedIndex;

    this.flkty[sliderId].slides.forEach((slide, index) => {
      slide.cells.forEach((cell) => {
        cell.element.querySelectorAll(selectors.links).forEach((link) => {
          link.setAttribute(attributes.tabIndex, selectedElementsIndex === index ? '0' : '-1');
        });
      });
    });
  }

  setSliderArrowsPosition(slider) {
    const arrows = slider.querySelectorAll(selectors.flickityButton);
    const image = slider.querySelector(selectors.productGridItemImage);

    if (arrows.length && image) {
      arrows.forEach((arrow) => {
        arrow.style.top = `${image.offsetHeight / 2}px`;
      });
    }
  }

  listen() {
    if (this.sliders.length) {
      this.checkSlidesSize();
      document.addEventListener('theme:resize:width', this.checkSlidesSizeOnResize);

      this.sliders.forEach((slider) => {
        slider.addEventListener('theme:tab:change', this.resetSliderEvent);
      });
    }
  }

  /**
   * Event callback for Theme Editor `section:block:select` event
   */
  onBlockSelect(evt) {
    const slider = evt.target.closest(selectors.slider);
    const flkty = Flickity.data(slider) || null;

    if (!slider) {
      return;
    }

    if (flkty !== null && flkty.isActive) {
      const index = parseInt([...evt.target.parentNode.children].indexOf(evt.target));
      const slidesPerPage = parseInt(flkty.slides[0].cells.length);
      const groupIndex = Math.floor(index / slidesPerPage);

      flkty.select(groupIndex);
    } else {
      const sliderStyle = slider.currentStyle || window.getComputedStyle(slider);
      const sliderPadding = parseInt(sliderStyle.paddingLeft);
      const blockPositionLeft = evt.target.offsetLeft - sliderPadding;

      // Native scroll to item
      slider.scrollTo({
        top: 0,
        left: blockPositionLeft,
        behavior: 'smooth',
      });
    }
  }

  /**
   * Event callback for Theme Editor `section:unload` event
   */
  onUnload() {
    if (this.flkty) {
      for (const key in this.flkty) {
        if (this.flkty.hasOwnProperty(key)) {
          this.flkty[key].destroy();
        }
      }
    }

    document.removeEventListener('theme:resize:width', this.checkSlidesSizeOnResize);

    if (this.sliders.length) {
      this.sliders.forEach((slider) => {
        slider.removeEventListener('theme:tab:change', this.resetSliderEvent);
      });
    }
  }
}

const gridSlider = {
  onLoad() {
    sections[this.id] = [];
    const els = this.container.querySelectorAll(selectors.slider);
    els.forEach((el) => {
      sections[this.id].push(new GridSlider(this.container));
    });
  },
  onUnload() {
    sections[this.id].forEach((el) => {
      if (typeof el.onUnload === 'function') {
        el.onUnload();
      }
    });
  },
  onBlockSelect(e) {
    sections[this.id].forEach((el) => {
      if (typeof el.onBlockSelect === 'function') {
        el.onBlockSelect(e);
      }
    });
  },
};

export {GridSlider, gridSlider};
